// textReadState — client for the SHARED Text-Inbox read ledger.
//
// The Text Inbox is in-house (admin/dispatch/super_admin) and reads are shared:
// one dispatcher reading a thread clears it for the whole team. This talks to
// the text-read-state function (one Firestore doc via admin SDK), so it behaves
// identically in Firestore and Supabase modes. A short poll keeps every open
// browser roughly in sync; a successful write pushes the merged ledger to local
// subscribers immediately so the reader's own UI updates without waiting.

import { authedFetch } from './forwardedSmsApi';
import { onTableChange } from './smsGateway';

export interface SharedTextRead {
  marks: Record<string, number>;
  floor: number;
}

const FN = '/.netlify/functions/text-read-state';
// Its pre-realtime cadence, kept until a payload proves the channel delivers.
const POLL_MS = 15_000;

const subs = new Set<(s: SharedTextRead) => void>();
let latest: SharedTextRead = { marks: {}, floor: 0 };
let off: (() => void) | null = null;
let inflight: Promise<void> | null = null;

const merge = (into: SharedTextRead, add: SharedTextRead): SharedTextRead => {
  const marks = { ...into.marks };
  for (const [k, v] of Object.entries(add.marks || {})) {
    marks[k] = Math.max(marks[k] || 0, Number(v) || 0);
  }
  return { marks, floor: Math.max(into.floor, Number(add.floor) || 0) };
};

const emit = () => { for (const cb of subs) cb(latest); };

// True once the first fetch has SETTLED (answered, or failed — a failure must not
// hide the badge for ever). Until then `latest` is just the empty default, and a
// consumer that counts unread against it shows every already-read thread as new.
let settledOnce = false;
export const sharedTextReadSettled = (): boolean => settledOnce;

async function fetchNow(): Promise<void> {
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      // authedFetch: a 401 is a session verdict (signs out → login) instead of a silent skip.
      const res = await authedFetch(FN);
      if (!res.ok) return; // offline / not dispatch — keep what we have
      const body = await res.json();
      latest = merge(latest, { marks: body?.marks || {}, floor: Number(body?.floor) || 0 });
      emit();
    } catch { /* offline — retry next poll */ }
    finally {
      inflight = null;
      if (!settledOnce) { settledOnce = true; emit(); }
    }
  })();
  return inflight;
}

/** Subscribe to the shared ledger. Fires immediately with the cached value,
 *  then on every poll/write. Returns an unsubscribe that stops polling once the
 *  last listener leaves. */
export function subscribeSharedTextRead(cb: (s: SharedTextRead) => void): () => void {
  subs.add(cb);
  cb(latest);
  fetchNow();
  // Seen on wolfdrivers.com (2026-09-10): this was a bare 15 s timer — a
  // Netlify call every 15 s per open tab, all night, whether or not anyone
  // had read anything. text_inbox_read_marks has been on the realtime
  // publication with a dispatch policy since 0011, so a change there is the
  // signal; the 15 s timer stays only until a payload proves the channel.
  if (!off) off = onTableChange('text_inbox_read_marks', () => { void fetchNow(); }, POLL_MS);
  return () => {
    subs.delete(cb);
    if (subs.size === 0 && off) { off(); off = null; }
  };
}

/** Advance shared marks/floor. Optimistically updates local subscribers, then
 *  POSTs (fire-and-forget — a failure just means the next poll reconciles). */
export function advanceSharedTextRead(patch: { marks?: Record<string, number>; floor?: number }): void {
  const before = latest;
  latest = merge(latest, { marks: patch.marks || {}, floor: patch.floor || 0 });
  // Nothing actually moved forward → skip the network round-trip.
  if (latest === before || JSON.stringify(latest) === JSON.stringify(before)) return;
  emit();
  (async () => {
    try {
      const res = await authedFetch(FN, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      if (res.ok) {
        const body = await res.json();
        latest = merge(latest, { marks: body?.marks || {}, floor: Number(body?.floor) || 0 });
        emit();
      }
    } catch { /* offline — next poll reconciles */ }
  })();
}

/** Current cached ledger (synchronous read for unread math). */
export function currentSharedTextRead(): SharedTextRead {
  return latest;
}
